import { AppHeader, Typography, List, ListItem, Button, Divider, Box } from '@ace-clublabs/shared-component-library';
import { useContext, useEffect, useState } from 'react';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { PartnerPromoCard } from '../components/PartnerSignUp/PartnerPromoCard';
import { ParkingSpotDisclaimer } from '../components/PartnerSignUp/ParkingSpotDisclaimer';
import { IPartnerIntegration, Pages, PartnerIntegration } from '@lib/types';
import { NewJoinSessionData, PartnerEnrollments } from '../application/models/NewJoinSessionData';
import {
  benefitsMap,
  imageMap,
  labelMap,
  PartnerEnrollmentProcessStatus,
  titleMap,
  registrationLinkMap,
  partnerHelper,
  fullyEnrolledImg,
  getPartnerBrandList,
} from '../utils/partnerEnrollmentHelper';
import { sources } from '../api/sources';
import LogCannon from '@ace-clublabs/log-cannon';
import { useHistory } from 'react-router-dom';
import { UtagHelper, UtagType } from '../utils/UtagHelper';
import { LoadingView } from '../components/LoadingView/LoadingView';

const PartnerEnrollmentPage: React.FC = () => {
  const history = useHistory();
  const {
    loadingInfo,
    loadingProvider,
    sessionData,
    refreshSession,
    membershipInfo,
    sendTag,
    updateMembershipSession,
    fetchPartnerOffers,
  } = useContext(MembershipAppStateContext);

  const [finishLoadingProps, setFinishLoadingProps] = useState({ finished: false, onFinish: () => {} });

  const partnerOffers = sessionData.partnerOffers?.availableOffers;
  const lastChoices = sessionData.formData.partnerEnrollments || ({} as PartnerEnrollments);

  const alreadyEnrolledWithAllPartners =
    partnerOffers?.every((partner) => lastChoices[partner.name as PartnerIntegration]) &&
    sessionData.completedPartnerEnrollment;

  const [partnerEnrollmentCheckboxValues, setPartnerEnrollmentCheckboxValues] = useState({
    ...lastChoices,
  } as PartnerEnrollments);

  const onCheckPartner = (partner: IPartnerIntegration, e: any) => {
    setPartnerEnrollmentCheckboxValues((prev) => ({
      ...prev,
      [partner.name]: e.target.checked,
    }));
  };

  const getInitialPartnerEnrollments = (partnerOffers: IPartnerIntegration[]): void => {
    const partnerEnrollments = {} as PartnerEnrollments;

    for (let offer of partnerOffers) {
      setPartnerEnrollmentCheckboxValues((prev) => ({
        ...prev,
        [offer.name]: !!offer.isSelectedByDefault,
      }));
    }
  };

  const parkingSpotOffer = partnerOffers?.find((partner) => partner.name === PartnerIntegration.PARKING_SPOT);

  const skipToConfirmationPage = () => {
    if (sessionData.completedRegistration) {
      updateMembershipSession({
        ...sessionData,
        completedPartnerEnrollment: true,
      });
    }
    history.push(Pages.ConfirmationPage);
  };

  useEffect(() => {
    const callFetchPartnerOffers = async () => {
      loadingProvider.startLoading('Fetching partner offers'); // to do update text
      const offers = await fetchPartnerOffers(sessionData.sessionId!, sessionData.partnerOffers!.enrollmentEmail!);
      if (offers?.length > 0) {
        getInitialPartnerEnrollments(offers);
      }
      setFinishLoadingProps({ finished: true, onFinish: () => loadingProvider.endLoading() });
    };

    if (!membershipInfo?.benefits) {
      refreshSession();
    } else {
      callFetchPartnerOffers();
    }
  }, [membershipInfo]);

  useEffect(() => {
    if (partnerOffers && partnerOffers.length === 0) {
      skipToConfirmationPage();
    } else if (partnerOffers?.length! > 0) {
      const page = 'partner enrollment';
      const partnerEnrollmentsTags = UtagHelper.buildPartnerIntegrationDefaultSelectionTags(
        sessionData,
        membershipInfo!
      );
      sendTag(UtagType.view, page, partnerEnrollmentsTags);
    }
  }, [partnerOffers]);

  useEffect(() => {
    const loading = loadingInfo.isLoading || !membershipInfo?.benefits || !partnerOffers;
    const body = document?.querySelector('body');

    if (!body) {
      return;
    }

    if (loading) {
      body.style.backgroundColor = '#FFFFFF';
    } else {
      body.style.backgroundColor = '#FAF8F4';
    }

    return () => {
      body.style.backgroundColor = '#FFFFFF';
    };
  }, [membershipInfo, loadingInfo.isLoading, partnerOffers]);

  const enrollPartners = async () => {
    const sessionId = sessionData.sessionId;

    const partnerEnrollmentsPayload = Object.keys(partnerEnrollmentCheckboxValues).map((partner) => ({
      name: partner as PartnerIntegration,
      isChecked: partnerEnrollmentCheckboxValues[partner as PartnerIntegration],
    }));

    const optedIntoEnrollment = partnerEnrollmentsPayload.some((enrollment) => enrollment.isChecked);
    if (!optedIntoEnrollment) {
      skipToConfirmationPage();
      return;
    }

    const equalToLastSelection = Object.keys(partnerEnrollmentCheckboxValues).every(
      (partner) =>
        lastChoices[partner as PartnerIntegration] === partnerEnrollmentCheckboxValues[partner as PartnerIntegration]
    );
    if (equalToLastSelection) {
      skipToConfirmationPage();
      return;
    }

    setFinishLoadingProps({ finished: false, onFinish: () => {} });
    loadingProvider.startLoading('Enrolling in partner offers');

    const { success, data } = await sources.postPartnerEnrollment({
      sessionId: sessionData?.sessionId!,
      partnerEnrollments: partnerEnrollmentsPayload,
    });

    const updatedSession: NewJoinSessionData = {
      ...sessionData,
      formData: {
        ...sessionData.formData,
        partnerEnrollments: partnerEnrollmentCheckboxValues,
      },
      completedPartnerEnrollment: true,
      partnerOffers: {
        ...sessionData.partnerOffers!,
      },
    };

    const enrolledInPartnerOffersSuccess =
      success && data?.partnerEnrollmentStatuses.some((enrollment) => enrollment.status === 'REQUESTED');

    if (!enrolledInPartnerOffersSuccess) {
      updateMembershipSession({
        ...updatedSession,
        partnerOffers: {
          ...updatedSession.partnerOffers!,
          enrollmentStatus: PartnerEnrollmentProcessStatus.FAILED,
        },
      });

      setFinishLoadingProps({
        finished: true,
        onFinish: () => {
          loadingProvider.endLoading();
          history.push(Pages.ConfirmationPage);
        },
      });

      return;
    }

    const { pollingStatus, partnerEnrollmentStatuses } = await partnerHelper.pollForPartnerEnrollmentStatus(sessionId!);

    if (pollingStatus === PartnerEnrollmentProcessStatus.COMPLETED) {
      LogCannon.blast('Partner enrollment completed', { partnerEnrollmentStatuses });
    } else {
      LogCannon.error('Partner enrollment failed to complete', { pollingStatus, partnerEnrollmentStatuses });
    }

    updateMembershipSession({
      ...updatedSession,
      partnerOffers: {
        ...updatedSession.partnerOffers!,
        enrollmentStatus: pollingStatus,
        partnerStatuses: partnerEnrollmentStatuses,
      },
    });

    setFinishLoadingProps({
      finished: true,
      onFinish: () => {
        loadingProvider.endLoading();
        history.push(Pages.ConfirmationPage);
      },
    });
  };

  const isLoading = loadingInfo.isLoading || !membershipInfo?.benefits || !partnerOffers;

  const loadingBarTime = partnerOffers && partnerOffers?.length > 0 ? 70 : 20;
  if (isLoading) {
    return (
      <LoadingView
        finishProps={finishLoadingProps}
        message={loadingInfo.loadingDetails[0] || 'Hang tight while we load your information'}
        subMessage={`Please don't refresh this page or press the back button.`}
        estimatedTimeInSeconds={loadingBarTime}
      />
    );
  }

  // split partnerIntegrations in two groups
  const [newIntegrationAccounts, existingIntegrationAccounts] = partnerOffers?.reduce<
    [IPartnerIntegration[], IPartnerIntegration[]]
  >(
    ([newAccounts, existingAccounts], integration) => {
      const isPreviouslyChecked = lastChoices[integration.name];
      const skip = !!isPreviouslyChecked && sessionData.completedPartnerEnrollment;

      if (integration.hasExistingAccount) {
        if (!skip) {
          existingAccounts.push(integration);
        }
      } else {
        if (!skip) {
          newAccounts.push(integration);
        }
      }
      return [newAccounts, existingAccounts];
    },
    [[], []]
  );

  const displayPartnerIntegrationCard = (
    partner: IPartnerIntegration,
    index: number,
    collection: IPartnerIntegration[],
    completedPartnerEnrollment: boolean,
    groupTitle?: string
  ) => {
    const { name } = partner;
    const isChecked = !!(
      (sessionData.formData.partnerEnrollments && partnerEnrollmentCheckboxValues[name]) ||
      partner.isSelectedByDefault
    );
    const lastInGroup = index + 1 === collection.length;

    return (
      <span key={index}>
        <PartnerPromoCard
          selected={isChecked}
          onClick={(e) => onCheckPartner(partner, e)}
          image={imageMap[name as PartnerIntegration]}
          linkUrl={registrationLinkMap[name]}
          label={labelMap[name]}
          firstInGroup={index === 0}
          lastInGroup={lastInGroup}
          groupTitle={groupTitle}
          partnerName={name}
        >
          <>
            <Typography variant={'b2Regular'} color="#707070">
              {titleMap[name]}
            </Typography>
            <PartnerPromoList benefits={benefitsMap[name as PartnerIntegration]} />
          </>
        </PartnerPromoCard>
        {!lastInGroup && (
          <Divider sx={{ width: 'calc(100% - 2rem)', margin: 'auto', opacity: 0.6 }} orientation="horizontal" />
        )}
      </span>
    );
  };

  return (
    <Box className="partner-enrollments-page">
      <AppHeader sx={{ padding: 0 }} />
      {alreadyEnrolledWithAllPartners ? (
        <>
          <Typography variant="b1Bold" className="partner-total-enrollment-title">
            You're already enrolled in{' '}
            <span dangerouslySetInnerHTML={{ __html: getPartnerBrandList(partnerOffers.map((offer) => offer.name)) }} />
            .
          </Typography>
          <img src={fullyEnrolledImg}></img>
        </>
      ) : (
        <Typography variant="b1Bold" className="partner-enrollment-title">
          Your AAA account is all set! Start enjoying exclusive AAA member benefits
        </Typography>
      )}
      {newIntegrationAccounts.map((partner, index) => {
        const groupTitle = 'Enroll for free:';
        return displayPartnerIntegrationCard(
          partner,
          index,
          newIntegrationAccounts,
          sessionData.completedPartnerEnrollment,
          groupTitle
        );
      })}
      {existingIntegrationAccounts.map((partner, index) => {
        const groupTitle = 'Add your AAA membership number to:';
        return displayPartnerIntegrationCard(
          partner,
          index,
          existingIntegrationAccounts,
          sessionData.completedPartnerEnrollment,
          groupTitle
        );
      })}

      <Button
        label="Continue"
        className="enroll-continue-btn"
        fullWidth
        onClick={enrollPartners}
        variant="contained"
        size="small"
        sx={{ margin: '1.5rem 0 1rem 0' }}
      ></Button>
      {parkingSpotOffer && !alreadyEnrolledWithAllPartners && (
        <ParkingSpotDisclaimer hasExistingAccount={!!parkingSpotOffer.hasExistingAccount} />
      )}
    </Box>
  );
};

export default PartnerEnrollmentPage;
export { PartnerEnrollmentPage };

export interface IPartnerPromoList {
  benefits: string[];
}

const PartnerPromoList = ({ benefits }: IPartnerPromoList) => {
  return (
    <List style={{ marginLeft: '0', margin: '0.5rem 0' }}>
      {benefits.map((benefit, index) => {
        return (
          <ListItem key={index}>
            <Typography component="span" sx={{ alignSelf: 'baseline', margin: '0 0.35rem' }}>
              {'\u2022'}
            </Typography>
            <Typography component="span" variant={'b2Regular'} color="#707070">
              {benefit}
            </Typography>
          </ListItem>
        );
      })}
    </List>
  );
};
